import React from 'react';

import { Formik } from 'formik';
import * as yup from 'yup';

import Header from 'components/AppBar';
import ApplicationOverlayContent from 'components/ApplicationLayout/ApplicationOverlayContent';
import Field from 'components/Field';
import Input from 'components/Input';
import ModalHeader from 'components/ModalHeader';
import { Desktop, Mobile } from 'components/Responsive';
import CustomSelect, { RenderProps as CustomSelectRenderProps } from 'components/Selects/CustomSelect';
import AirportSearch from 'containers/AirportsSearch';
import CurrencyInput from 'containers/CurrencyInput';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useToast from 'hooks/useToast';
import { Airport } from 'models/airport';
import { SpecificRoute as SpecificRouteModel } from 'models/specificRoute';

import { Scaffold, Container, About, Form, Fields, ButtonArea, ConfirmButton } from './styles';

interface RouteForm {
  departurePoint?: Airport;
  arrivalPoint?: Airport;
  maxPrice: number;
  specificFlightAnticipationDays?: number;
  specificFlightFlexibility?: number;
}

interface Props {
  show: boolean;
  onClose: () => void;
  onConfirm: (data: SpecificRouteModel) => void;
}

const SpecificRoute = ({ show, onConfirm, onClose }: Props) => {
  const toast = useToast();
  const { t } = useStoreTranslation();

  const initialValues: RouteForm = {
    departurePoint: undefined,
    arrivalPoint: undefined,
    maxPrice: 0,
    specificFlightAnticipationDays: undefined,
    specificFlightFlexibility: undefined,
  };

  const validationSchema = yup.object().shape({
    departurePoint: yup.object().required(),
    arrivalPoint: yup.object().required(),
    maxPrice: yup.number().min(0),
    specificFlightAnticipation: yup.number().min(0),
    specificFlightFlexibility: yup.number().min(0),
  });

  const handlePointSetValue = (airport: Airport) => (airport ? airport.related_city : '');

  const handleRemoveFlexibilityMask = (value: string) => value.replace('+ ', '');

  const handleSubmit = (data: RouteForm) => {
    onConfirm({
      departurePoint: data.departurePoint?.related_city,
      departurePointCode: data.departurePoint?.code,
      arrivalPoint: data.arrivalPoint?.related_city,
      arrivalPointCode: data.arrivalPoint?.code,
      maxPrice: data.maxPrice ? data.maxPrice : '',
      specificFlightFlexibility: data.specificFlightFlexibility ? data.specificFlightFlexibility : '',
      specificFlightAnticipationDays: data.specificFlightAnticipationDays ? data.specificFlightAnticipationDays : '',
    });
    onClose();
    toast.add({ type: 'success', message: t('rule.specificRoute.addSuccess') });
  };

  return (
    <>
      <ApplicationOverlayContent sidebar show={show} onClose={onClose}>
        <Scaffold>
          <Mobile>
            <Header goBack={onClose}>{t('rule.specificRoute.header')}</Header>
          </Mobile>

          <Desktop>
            <ModalHeader title={t('rule.specificRoute.header')} onClose={onClose} />
          </Desktop>

          <Container>
            <About>
              <About.Title>{t('rule.specificRoute.aboutTitle')}</About.Title>
              <About.Text>{t('rule.specificRoute.aboutText')}</About.Text>
              <About.Text>{t('rule.specificRoute.aboutTextSecondParagraph')}</About.Text>
              <About.Text>{t('rule.specificRoute.aboutTextThirdParagraph')}</About.Text>
            </About>
            <Formik
              initialValues={initialValues}
              isInitialValid={(formik: any) => validationSchema.isValidSync(formik.initialValues)}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({ isValid }) => (
                <Form>
                  <Fields>
                    <Field name="departurePoint" label={t('rule.specificRoute.departurePoint')}>
                      <CustomSelect
                        noIcon
                        placeHolder={t('rule.specificRoute.departurePointPlaceHolder')}
                        onSetValue={handlePointSetValue}
                      >
                        {(selectProps: CustomSelectRenderProps) => (
                          <AirportSearch
                            type="DEPARTURE"
                            {...selectProps}
                            placeHolder={t('rule.specificRoute.departurePointPlaceHolder')}
                          />
                        )}
                      </CustomSelect>
                    </Field>
                    <Field name="arrivalPoint" label={t('rule.specificRoute.arrivalPoint')}>
                      <CustomSelect
                        noIcon
                        placeHolder={t('rule.specificRoute.arrivalPointPlaceHolder')}
                        onSetValue={handlePointSetValue}
                      >
                        {(selectProps: CustomSelectRenderProps) => (
                          <AirportSearch
                            type="ARRIVAL"
                            {...selectProps}
                            placeHolder={t('rule.specificRoute.arrivalPointPlaceHolder')}
                          />
                        )}
                      </CustomSelect>
                    </Field>
                    <Field name="specificFlightAnticipationDays" label={t('rule.restriction.anticipation')}>
                      <Input
                        darkPlaceholder
                        suffix={t('rule.restriction.anticipationSuffix')}
                        placeholder={t('rule.restriction.withoutRestriction')}
                      />
                    </Field>
                    <Field
                      name="maxPrice"
                      label={t('rule.specificRoute.maxPrice')}
                      simpleTooltip
                      simpleTooltipText={t('rule.tour.flightMaxPrice')}
                    >
                      <CurrencyInput placeHolder={t('rule.restriction.withoutRestriction')} />
                    </Field>
                    <Field
                      name="specificFlightFlexibility"
                      label={t('rule.restriction.flexibility')}
                      removeMask={handleRemoveFlexibilityMask}
                    >
                      <Input
                        suffix="%"
                        darkPlaceholder
                        placeholder={t('rule.restriction.withoutRestriction')}
                        mask="+ 999"
                      />
                    </Field>
                  </Fields>
                  <ButtonArea>
                    <ConfirmButton disabled={!isValid} value={t('rule.specificRoute.confirm')} />
                  </ButtonArea>
                </Form>
              )}
            </Formik>
          </Container>
        </Scaffold>
      </ApplicationOverlayContent>
    </>
  );
};

export default SpecificRoute;
